/**
 * Activity Feed
 * 
 * WHY: Analysts working in teams need visibility into recent vetting actions.
 * This feed shows what observations have been vetted, by whom, and when,
 * enabling coordination and reducing duplicate work.
 * 
 * @see .docs/architecture/phase5_security_assessment.md - Phase 6 Requirements
 */

import { useQuery } from '@tanstack/react-query'
import { api, type Observation } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CheckCircle,
  XCircle,
  Shield,
  Clock,
  AlertTriangle,
  Activity,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ActivityFeedProps {
  className?: string
  /** Maximum number of items to display */
  limit?: number
}

interface ActivityItem {
  id: string
  title: string
  status: string
  severity: string
  analystName?: string
  timestamp: string
}

/**
 * Get icon and color for observation status.
 */
function getStatusDisplay(status: string) {
  switch (status.toLowerCase()) {
    case 'confirmed':
      return {
        icon: AlertTriangle,
        color: 'text-destructive',
        bgColor: 'bg-destructive/10',
        label: 'Confirmed',
      }
    case 'false_positive':
      return {
        icon: XCircle,
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
        label: 'False Positive',
      }
    case 'mitigated':
      return {
        icon: Shield,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
        label: 'Mitigated',
      }
    case 'deferred':
      return {
        icon: Clock,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500/10',
        label: 'Deferred',
      }
    default:
      return {
        icon: CheckCircle,
        color: 'text-muted-foreground',
        bgColor: 'bg-muted',
        label: status,
      }
  }
}

/**
 * Format timestamp for display.
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Get severity badge variant.
 */
function getSeverityVariant(severity: string): 'critical' | 'high' | 'medium' | 'low' | 'default' {
  const lower = severity.toLowerCase()
  if (lower === 'critical') return 'critical'
  if (lower === 'high') return 'high'
  if (lower === 'medium') return 'medium'
  if (lower === 'low') return 'low'
  return 'default'
}

/**
 * Transform observations to activity items.
 */
function transformToActivityItems(observations: Observation[], limit: number): ActivityItem[] {
  return observations
    .filter((obs) => obs.status !== 'pending') // Only show vetted items
    .sort((a, b) => {
      // Sort by most recently vetted (use vetted_at if available, fallback to created_at)
      const dateA = new Date(a.vetted_at || a.created_at).getTime()
      const dateB = new Date(b.vetted_at || b.created_at).getTime()
      return dateB - dateA
    })
    .slice(0, limit)
    .map((obs) => ({
      id: obs.observation_id,
      title: obs.title,
      status: obs.status,
      severity: obs.severity,
      analystName: obs.vetted_by,
      timestamp: obs.vetted_at || obs.created_at,
    }))
}

/**
 * Individual activity item display.
 */
function ActivityItemRow({ item }: { item: ActivityItem }) {
  const statusDisplay = getStatusDisplay(item.status)
  const StatusIcon = statusDisplay.icon

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <div className={cn('p-2 rounded-full', statusDisplay.bgColor)}>
        <StatusIcon className={cn('h-4 w-4', statusDisplay.color)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm truncate">{item.title}</p>
          <Badge variant={getSeverityVariant(item.severity)} className="text-xs">
            {item.severity}
          </Badge>
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <span className={statusDisplay.color}>{statusDisplay.label}</span>
          {item.analystName && (
            <>
              <span>•</span>
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {item.analystName}
              </span>
            </>
          )}
          <span>•</span>
          <span>{formatTimestamp(item.timestamp)}</span>
        </div>
      </div>
    </div>
  )
}

export function ActivityFeed({ className, limit = 10 }: ActivityFeedProps) {
  // Fetch all observations (we filter for vetted ones)
  const { data: observations, isLoading, error } = useQuery({
    queryKey: ['softwareiq', 'observations', 'activity'],
    queryFn: () => api.softwareiq.observations.list(),
    staleTime: 10_000, // 10 seconds - activity should be fairly fresh
    refetchInterval: 30_000, // Auto-refresh every 30 seconds
  })

  const activityItems = observations ? transformToActivityItems(observations, limit) : []

  if (error) {
    return (
      <Card className={cn('border-destructive/50', className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Activity className="h-5 w-5" />
            Error Loading Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Failed to load activity feed'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Recent Activity
            </CardTitle>
            <CardDescription>Latest vetting decisions</CardDescription>
          </div>
          {!isLoading && (
            <Badge variant="outline" className="text-xs">
              {activityItems.length} items
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : activityItems.length > 0 ? (
          <div className="max-h-[400px] overflow-y-auto -mx-4 px-4">
            {activityItems.map((item) => (
              <ActivityItemRow key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No recent activity</p>
            <p className="text-xs mt-1">Vetting decisions will appear here</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
